import { Stack, Typography } from '@mui/material';
import { AngleUnits } from 'convert-units/definitions/angle';
import { compact, find, groupBy, isEmpty, lowerCase, map, maxBy, mean, minBy, uniq, upperFirst } from 'lodash';
import { ReactNode, createContext, useCallback, useContext, useMemo } from 'react';

import { CELLULAR_SIGNAL_POOR_SIGNAL_THRESHOLD, CELLULAR_SIGNAL_WEAK_THRESHOLD } from '@/api/devices/useDevice';
import { ProcessingSource } from '@/api/historicalData';
import { Line } from '@/charts';
import { GenericObject, getYValue } from '@/charts/helpers';
import { convert } from '@/common/convert';
import { LineIcon, WhiskerLimitIcon } from '@/components/data/ColorIcon';
import { Datum, unit, unitType } from '@/components/data/Datum';
import { DirectionDatum } from '@/components/data/DirectionDatum';
import { DirectionIconProps } from '@/components/data/DirectionIcon';
import {
    DataDetailDatum,
    DataDetailsSection,
} from '@/components/spotter/historicalDataTab/datumDetail/DataDetailPanel';
import { DataDetailTimestampRange } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailSectionTimestamp';
import { SelectableTimestampBox } from '@/components/spotter/historicalDataTab/datumDetail/SelectableTimestampBox';
import { DateRange } from '@/store';
import { isFiniteNumber, meanDirection } from '@/utils';

type Datum = { timestamp: string; processing_source?: ProcessingSource };

export type LineChartDataSummaryProps<D, L extends Line<D>> = {
    dateRange: DateRange | null;
    dataset: D[];
    lines: L[];
    renderLineSummary: (arg: { line: L }) => ReactNode;
};

export type DataLineContext = { line: Line<GenericObject>; dataset: GenericObject[]; values: number[] };

export const DataLineContext = createContext<DataLineContext>({
    line: {} as Line<GenericObject>,
    dataset: [],
    values: [],
});

export const LineChartDataSummary = <D extends GenericObject, L extends Line<D>>({
    dateRange,
    dataset,
    lines,
    renderLineSummary,
}: LineChartDataSummaryProps<D, L>) => {
    const lineValuesBySensorLabel = useMemo(() => {
        if (!dateRange?.startDate) {
            return null;
        }

        const multipleSensorPositions = uniq(map(dataset, 'sensorPosition')).length > 1;

        const lineValues = lines.map((line) => {
            const { valueKey, dataFilter } = line;
            const lineData = dataFilter ? dataset.filter(dataFilter) : dataset;
            const values = compact(map(lineData, valueKey)) as number[];
            if (!values.length) {
                return null;
            }

            return {
                line,
                values,
                lineData,
                sensorPosition: upperFirst(lowerCase(String(lineData?.[0]?.sensorPosition))),
            };
        });
        return groupBy(
            compact(lineValues),
            ({ line, sensorPosition }) =>
                line.sensorLabel ?? (multipleSensorPositions ? sensorPosition : undefined) ?? ''
        );
    }, [dataset, dateRange?.startDate, lines]);

    if (!dateRange?.startDate || isEmpty(lineValuesBySensorLabel)) return false;

    return (
        <>
            <DataDetailTimestampRange dateRange={dateRange} />
            <Stack sx={{ m: 1 }}>
                {map(lineValuesBySensorLabel, (lineValues, sensorLabel) => (
                    <DataDetailsSection key={sensorLabel} label={sensorLabel}>
                        {lineValues.map(({ line, values, lineData }) => (
                            <DataLineContext.Provider
                                key={line.id}
                                value={{ line: line as Line<GenericObject>, dataset: lineData as D[], values }}
                            >
                                <Stack>{renderLineSummary({ line })}</Stack>
                            </DataLineContext.Provider>
                        ))}
                    </DataDetailsSection>
                ))}
            </Stack>
        </>
    );
};

export type DataLineSummaryDatumProps = { label?: string; valueKey?: string; icon?: ReactNode };

export const DataLineMean = ({ label, icon, valueKey: valueKeyProp }: DataLineSummaryDatumProps) => {
    const { dataset, line, values } = useContext(DataLineContext);
    const { label: lineLabel, color } = line;
    const valueKey = valueKeyProp ?? line.valueKey;
    const datum = find(dataset, (d) => isFiniteNumber(d[valueKey]));
    const resolvedLabel = useMemo(() => {
        if (label) return label;
        return `Averaged ${lineLabel.toLowerCase()}`;
    }, [label, lineLabel]);
    if (!datum) return false;
    const meanValue = mean(values);

    return (
        <DataDetailDatum label={resolvedLabel} icon={icon ?? <LineIcon dot color={color.line} />}>
            <Datum value={meanValue} type={unitType(line, datum)} sourceUnit={unit(line, datum)} />
        </DataDetailDatum>
    );
};

export const DataLineDirectionMean = ({
    label,
    icon,
    valueKey: valueKeyProp,
    type,
    ...units
}: DataLineSummaryDatumProps & { type: DirectionIconProps['type'] } & ({ unit: string } | { unitKey: string })) => {
    const { dataset, line, values } = useContext(DataLineContext);
    const { label: lineLabel } = line;
    const valueKey = valueKeyProp ?? line.valueKey;
    const datum = find(dataset, (d) => isFiniteNumber(d[valueKey]));
    const resolvedLabel = useMemo(() => {
        if (label) return label;
        return `Averaged ${lineLabel.toLowerCase()}`;
    }, [label, lineLabel]);
    if (!datum) return false;
    const dirUnit = unit(units, datum) as AngleUnits;

    const meanValue = meanDirection(values.map((v) => convert(v).from(dirUnit).to('deg')));

    return (
        <DataDetailDatum label={resolvedLabel} icon={icon}>
            <DirectionDatum iconSx={{ fontSize: '1em' }} heading={meanValue} type={type} unit={dirUnit} />
        </DataDetailDatum>
    );
};

export type DataLineExtentRendererParams = { datum: GenericObject; end: 'min' | 'max' };

export const DataLineExtent = ({
    valueKey: valueKeyProp,
    label,
    minProps,
    maxProps,
    datumRenderer,
}: DataLineSummaryDatumProps & {
    minProps?: DataLineSummaryDatumProps;
    maxProps?: DataLineSummaryDatumProps;
    datumRenderer?: (arg: DataLineExtentRendererParams) => ReactNode;
}) => {
    const { dataset, line } = useContext(DataLineContext);
    const { color, label: lineLabel } = line;
    const minValueKey = minProps?.valueKey ?? valueKeyProp ?? line.valueKey;
    const maxValueKey = maxProps?.valueKey ?? valueKeyProp ?? line.valueKey;

    const minLabel = useMemo(
        () => minProps?.label ?? `Min ${(label ?? lineLabel).toLowerCase()}`,
        [label, lineLabel, minProps?.label]
    );

    const maxLabel = useMemo(
        () => maxProps?.label ?? `Max ${(label ?? lineLabel).toLowerCase()}`,
        [label, lineLabel, maxProps?.label]
    );

    const min = minBy(dataset, minValueKey);
    const max = maxBy(dataset, maxValueKey);

    const renderDatum = useCallback(
        ({ datum, end }: DataLineExtentRendererParams) =>
            datumRenderer?.({ datum, end }) ?? (
                <Datum
                    value={getYValue(datum, end === 'max' ? maxValueKey : minValueKey)}
                    type={unitType(line, datum)}
                    sourceUnit={unit(line, datum)}
                />
            ),
        [datumRenderer, line, maxValueKey, minValueKey]
    );

    return (
        <>
            {max && (
                <SelectableTimestampBox timestamp={max.timestamp as string}>
                    <DataDetailDatum
                        label={maxLabel}
                        icon={maxProps?.icon ?? <WhiskerLimitIcon color={color.line} end="max" />}
                    >
                        {renderDatum({ datum: max, end: 'max' })}
                    </DataDetailDatum>
                </SelectableTimestampBox>
            )}
            {min && (
                <SelectableTimestampBox timestamp={min.timestamp as string}>
                    <DataDetailDatum
                        label={minLabel}
                        icon={minProps?.icon ?? <WhiskerLimitIcon color={color.line} end="min" />}
                    >
                        {renderDatum({ datum: min, end: 'min' })}
                    </DataDetailDatum>
                </SelectableTimestampBox>
            )}
        </>
    );
};

export const DataLineCellularSignalConsistency = () => {
    const { values } = useContext(DataLineContext);

    const { weakSignalPercent, poorSignalPercent } = useMemo(() => {
        if (!values.length) {
            return { weakSignalPercent: 0, poorSignalPercent: 0 };
        }

        const poorSignalCount = values.filter((v) => v < CELLULAR_SIGNAL_POOR_SIGNAL_THRESHOLD).length;
        const weakSignalCount = values.filter(
            (v) => v >= CELLULAR_SIGNAL_POOR_SIGNAL_THRESHOLD && v < CELLULAR_SIGNAL_WEAK_THRESHOLD
        ).length;

        const weakSignal = (weakSignalCount / values.length) * 100;
        const poorSignal = (poorSignalCount / values.length) * 100;

        return {
            weakSignalPercent: parseFloat(weakSignal.toFixed(1)),
            poorSignalPercent: parseFloat(poorSignal.toFixed(1)),
        };
    }, [values]);

    return (
        <>
            <DataDetailDatum label="Time with weak signal" sx={{ 'div:first-of-type': { ml: 2.5 } }}>
                <Typography component="span" sx={{ whiteSpace: 'nowrap' }}>
                    {weakSignalPercent}%
                </Typography>
            </DataDetailDatum>
            <DataDetailDatum label="Time with poor signal" sx={{ 'div:first-of-type': { ml: 2.5 } }}>
                <Typography component="span" sx={{ whiteSpace: 'nowrap' }}>
                    {poorSignalPercent}%
                </Typography>
            </DataDetailDatum>
        </>
    );
};
